/**
 * NFL prop presets for the admin prediction tool.
 *
 * Every preset here is AUTO-SETTLEABLE: its `statCategory` is one the NFL
 * manual-prop resolver knows (see services/stats/nfl-manual-prop-stats), and
 * the stat behind it is a verified MySportsFeeds team box-score field. The
 * guard test in this module's spec fails if a preset ever names a category the
 * resolver can't resolve — a prop we can sell but never settle is the failure
 * mode worth designing against.
 *
 * Unlike the soccer presets, these carry the STRUCTURED fields
 * (propType + statCategory + threshold) rather than only free text, which is
 * what lets them settle at all.
 *
 * No base rates. The soccer presets quote real FIFA tournament rates; we have
 * no equivalent NFL distribution yet, and a fabricated number shown on an
 * admin screen reads as authoritative. Thresholds below are round-number
 * starting points an admin is expected to adjust per game.
 */

// Categories come from the shared stat catalog rather than the settlement
// module, so this file carries no server-side dependency and mirrors cleanly
// into jest-client. The spec still checks every preset against the real
// resolver.
import type { NflTeamStatCategoryId } from './manual-stat-options';

export interface NflPropPreset {
  key: string;
  /** Button text. */
  label: string;
  /** One line under the label — what settles it. */
  hint: string;
  /** MANUAL_MATCH_THRESHOLD = whole game; MANUAL_TEAM_THRESHOLD = one side. */
  propType: 'MANUAL_MATCH_THRESHOLD' | 'MANUAL_TEAM_THRESHOLD';
  statCategory: NflTeamStatCategoryId;
  /** Starting line. A .5 line cannot push; a whole number can. */
  defaultThreshold: number;
  /** Step for the threshold input — yards move in bigger jumps than counts. */
  thresholdStep: number;
  suggestedCostCoins: number;
  suggestedMultiplier: number;
  /** Rendered into the description, e.g. "touchdowns" / "passing yards". */
  unitLabel: string;
}

export const NFL_PROP_PRESETS: NflPropPreset[] = [
  // ---- Game level -------------------------------------------------------
  {
    key: 'game-total-touchdowns',
    label: 'Over X touchdowns (game)',
    hint: 'Both teams · settles from the team box score',
    propType: 'MANUAL_MATCH_THRESHOLD',
    statCategory: 'GAME_TOTAL_TOUCHDOWNS',
    defaultThreshold: 4.5,
    thresholdStep: 0.5,
    suggestedCostCoins: 50,
    suggestedMultiplier: 2.0,
    unitLabel: 'touchdowns',
  },
  {
    key: 'game-total-field-goals',
    label: 'Over X field goals (game)',
    hint: 'Both teams · settles from the team box score',
    propType: 'MANUAL_MATCH_THRESHOLD',
    statCategory: 'GAME_TOTAL_FIELD_GOALS',
    defaultThreshold: 3.5,
    thresholdStep: 0.5,
    suggestedCostCoins: 50,
    suggestedMultiplier: 2.0,
    unitLabel: 'field goals',
  },
  {
    key: 'game-total-passing-yards',
    label: 'Over X passing yards (game)',
    hint: 'Both teams, NET of sacks',
    propType: 'MANUAL_MATCH_THRESHOLD',
    statCategory: 'GAME_TOTAL_PASSING_YARDS',
    defaultThreshold: 450,
    thresholdStep: 25,
    suggestedCostCoins: 50,
    suggestedMultiplier: 2.0,
    unitLabel: 'passing yards',
  },
  {
    key: 'game-total-rushing-yards',
    label: 'Over X rushing yards (game)',
    hint: 'Both teams',
    propType: 'MANUAL_MATCH_THRESHOLD',
    statCategory: 'GAME_TOTAL_RUSHING_YARDS',
    defaultThreshold: 220,
    thresholdStep: 10,
    suggestedCostCoins: 50,
    suggestedMultiplier: 2.0,
    unitLabel: 'rushing yards',
  },

  // ---- Team specific ----------------------------------------------------
  {
    key: 'team-passing-yards',
    label: 'Over X passing yards (one team)',
    hint: 'Pick a team · NET of sacks, per the official box score',
    propType: 'MANUAL_TEAM_THRESHOLD',
    statCategory: 'TEAM_PASSING_YARDS',
    defaultThreshold: 225,
    thresholdStep: 25,
    suggestedCostCoins: 50,
    suggestedMultiplier: 2.0,
    unitLabel: 'passing yards',
  },
  {
    key: 'team-rushing-yards',
    label: 'Over X rushing yards (one team)',
    hint: 'Pick a team',
    propType: 'MANUAL_TEAM_THRESHOLD',
    statCategory: 'TEAM_RUSHING_YARDS',
    defaultThreshold: 110,
    thresholdStep: 10,
    suggestedCostCoins: 50,
    suggestedMultiplier: 2.0,
    unitLabel: 'rushing yards',
  },
  {
    key: 'team-touchdowns',
    label: 'Over X touchdowns (one team)',
    hint: 'Pick a team',
    propType: 'MANUAL_TEAM_THRESHOLD',
    statCategory: 'TEAM_TOUCHDOWNS',
    defaultThreshold: 2.5,
    thresholdStep: 0.5,
    suggestedCostCoins: 50,
    suggestedMultiplier: 2.0,
    unitLabel: 'touchdowns',
  },
  {
    key: 'team-field-goals',
    label: 'Over X field goals (one team)',
    hint: 'Pick a team',
    propType: 'MANUAL_TEAM_THRESHOLD',
    statCategory: 'TEAM_FIELD_GOALS',
    defaultThreshold: 1.5,
    thresholdStep: 0.5,
    suggestedCostCoins: 50,
    suggestedMultiplier: 2.0,
    unitLabel: 'field goals',
  },
];

export function findNflPreset(key: string): NflPropPreset | undefined {
  return NFL_PROP_PRESETS.find((p) => p.key === key);
}
